import { useEffect, useMemo, useRef, useState } from "react";
import { Plus, Trash, X, Bell, CheckCircle, List as ListIcon, CalendarBlank, GridFour, MagnifyingGlass, CaretDown, DownloadSimple, ClockClockwise, PencilSimple, AirplaneTilt, CaretUp, MapPin } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../../lib/api";
import { useCurrency } from "../../lib/useCurrency";
import { useTrainerTimezone, formatTimeInTz, shortTzLabel } from "../../lib/useTrainerTimezone";
import ScheduleCalendar from "../../components/ScheduleCalendar";
import { BookingsModal as GroupBookingsModal } from "./TrainerGroupSessions";
import SeriesCancelDialog from "../../components/SeriesCancelDialog";
import CalendarSync from "../../components/CalendarSync";
import { Popover, PopoverTrigger, PopoverContent } from "../../components/ui/popover";
import { Calendar } from "../../components/ui/calendar";

const SESSION_TYPES = [
  { label: "60 Minutes", duration: 60, defaultPrice: 80 },
  { label: "45 Minutes", duration: 45, defaultPrice: 60 },
  { label: "30 Minutes", duration: 30, defaultPrice: 40 },
  { label: "Group Session", duration: 60, defaultPrice: 25 },
];

export default function TrainerSchedule() {
  const { symbol } = useCurrency();
  const trainerTz = useTrainerTimezone();
  const [sessions, setSessions] = useState([]);
  const [clients, setClients] = useState([]);
  const [staff, setStaff] = useState([]);
  const [awayPeriods, setAwayPeriods] = useState([]);
  const [awayExpanded, setAwayExpanded] = useState(false);
  const [locationList, setLocationList] = useState([]);
  const [locationFilter, setLocationFilter] = useState(() => {
    try {
      return window.localStorage.getItem("traina.locationFilter") || "all";
    } catch { return "all"; }
  });
  // Persist chip selection so a coach who mostly runs a single studio doesn't
  // have to re-click it every visit.
  useEffect(() => {
    try { window.localStorage.setItem("traina.locationFilter", locationFilter); } catch { /* noop */ }
  }, [locationFilter]);
  const [showForm, setShowForm] = useState(false);
  const [formInitial, setFormInitial] = useState(null); // { date, duration } | Date | null
  const [editingSession, setEditingSession] = useState(null);
  const [cancellingSession, setCancellingSession] = useState(null); // { session, action }
  const [layout, setLayout] = useState("list"); // list | 1day | 3day | 7day | month
  const [view, setView] = useState("upcoming"); // upcoming | archive (only used in list layout)
  const [filter, setFilter] = useState("all");

  const load = async () => {
    try {
      const [s, c, st, away, locs, gc] = await Promise.all([
        api.get("/sessions"),
        api.get("/clients"),
        api.get("/staff"),
        api.get("/trainers/me/away?days=90").catch(() => ({ data: { periods: [] } })),
        api.get("/locations").catch(() => ({ data: [] })),
        api.get("/group/classes").catch(() => ({ data: [] })),
      ]);
      setSessions(s.data);
      setClients(c.data);
      setStaff(st.data.filter((x) => x.active));
      setAwayPeriods(away.data?.periods || []);
      setLocationList(locs.data || []);
      setGroupClasses(Array.isArray(gc.data) ? gc.data : []);
    } catch (_e) {}
  };
  const [groupClasses, setGroupClasses] = useState([]);
  const [viewingClass, setViewingClass] = useState(null);
  const groupItems = useMemo(() => groupClasses.map((c) => ({
    id: `gc-${c.id}`, _group: true, _cls: c,
    start_time: new Date(c.start).toISOString(), duration_minutes: c.duration_min,
    session_type: `Group · ${c.booked_count}/${c.capacity} booked${c.waitlist_count ? ` · ${c.waitlist_count} waiting` : ""}`,
    client: { name: c.title }, price: c.price, location: c.location,
    status: new Date(c.start) < new Date() ? "completed" : "scheduled",
    assigned_staff_id: c.instructor_id || null,
  })), [groupClasses]);

  useEffect(() => { load(); }, []);

  // Apply the location filter chip to everything downstream — list, calendar, counts.
  const filteredSessions = useMemo(() => {
    if (locationFilter === "all") return sessions;
    if (locationFilter === "__none") return sessions.filter((s) => !s.location);
    return sessions.filter((s) => s.location === locationFilter);
  }, [sessions, locationFilter]);

  const nowIso = new Date().toISOString();
  const upcomingAll = useMemo(
    () => [...filteredSessions.filter((s) => s.status === "scheduled" && s.start_time >= nowIso), ...groupItems.filter((g) => g.start_time >= nowIso)]
      .sort((a, b) => a.start_time.localeCompare(b.start_time)),
    [filteredSessions, groupItems, nowIso]
  );
  const archiveAll = useMemo(
    () => [...filteredSessions.filter((s) => !(s.status === "scheduled" && s.start_time >= nowIso)), ...groupItems.filter((g) => g.start_time < nowIso)]
      .sort((a, b) => b.start_time.localeCompare(a.start_time)),
    [filteredSessions, groupItems, nowIso]
  );

  const filtered = useMemo(() => {
    const pool = view === "upcoming" ? upcomingAll : archiveAll;
    if (filter === "all") return pool;
    return pool.filter((s) => s.status === filter);
  }, [view, upcomingAll, archiveAll, filter]);

  // Reset secondary filter when view switches
  useEffect(() => { setFilter("all"); }, [view]);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/sessions/${id}`, { status });
      toast.success(`Marked ${status}`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this session?")) return;
    try {
      await api.delete(`/sessions/${id}`);
      toast.success("Session deleted");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const sendReminder = async (id) => {
    try {
      await api.post("/notifications/send", { session_id: id, channels: ["email"] });
      toast.success("Reminder sent");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const downloadIcs = async (id) => {
    try {
      const res = await api.get(`/calendar/sessions/${id}.ics`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/calendar" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `session-${id.slice(0, 8)}.ics`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const openFormAt = (dt, duration) => {
    setFormInitial(duration ? { date: dt, duration } : dt);
    setShowForm(true);
  };

  const onSessionMove = async (session, newDt) => {
    const newIso = newDt.toISOString();
    const prettyOld = new Date(session.start_time).toLocaleString([], { weekday: "short", hour: "2-digit", minute: "2-digit" });
    const prettyNew = newDt.toLocaleString([], { weekday: "short", hour: "2-digit", minute: "2-digit" });
    if (Math.abs(new Date(session.start_time).getTime() - newDt.getTime()) < 60000) return;
    try {
      await api.patch(`/sessions/${session.id}`, { start_time: newIso });
      toast.success(`Moved ${session.client?.name || "session"} — ${prettyOld} → ${prettyNew}`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      load();
    }
  };

  const onSessionResize = async (session, newDuration) => {
    if (!newDuration || newDuration === session.duration_minutes) return;
    try {
      await api.patch(`/sessions/${session.id}`, { duration_minutes: newDuration });
      toast.success(`${session.client?.name || "Session"} duration → ${newDuration} min`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      load();
    }
  };
  const closeForm = () => {
    setShowForm(false);
    setFormInitial(null);
  };

  const requestCancel = (s) => {
    if (!s) return;
    setCancellingSession({ session: s, action: "cancel" });
  };

  const requestDelete = (s) => {
    if (!s) return;
    setCancellingSession({ session: s, action: "delete" });
  };

  const confirmCancelOrDelete = async (scope) => {
    if (!cancellingSession) return;
    const { session, action } = cancellingSession;
    try {
      if (scope === "this") {
        // Single occurrence — use existing endpoints
        if (action === "cancel") await updateStatus(session.id, "cancelled");
        else await remove(session.id);
      } else {
        const { data } = await api.post(`/sessions/${session.id}/cancel-series`, { scope, action });
        toast.success(`${action === "delete" ? "Deleted" : "Cancelled"} ${data.affected} session${data.affected === 1 ? "" : "s"}${data.skipped_locked?.length ? ` · ${data.skipped_locked.length} locked skipped` : ""}`);
        load();
      }
      setCancellingSession(null);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const onCalendarAction = (action, s) => {
    if (s?._group) {
      if (action === "open" || action === "edit" || action === "group") return setViewingClass(s._cls);
      return;
    }
    if (action === "remind") return sendReminder(s.id);
    if (action === "complete") return updateStatus(s.id, "completed");
    if (action === "cancel") return requestCancel(s);
    if (action === "delete") return requestDelete(s);
    if (action === "edit" || action === "open") return setEditingSession(s);
  };

  return (
    <div className="space-y-8" data-testid="trainer-schedule">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="tracker text-brand mb-3">[ Schedule ]</div>
          <h1 className="font-display text-4xl sm:text-5xl">Sessions.</h1>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex flex-wrap" data-testid="schedule-layout-toggle">
            {[
              { key: "list", label: "List", Icon: ListIcon },
              { key: "1day", label: "Day", Icon: ClockClockwise },
              { key: "3day", label: "3 Day", Icon: CalendarBlank },
              { key: "7day", label: "Week", Icon: CalendarBlank },
              { key: "month", label: "Month", Icon: GridFour },
            ].map(({ key, label, Icon }) => (
              <button
                key={key}
                onClick={() => setLayout(key)}
                className={`tracker px-3 py-2 border flex items-center gap-1.5 ${layout === key ? "bg-brand border-brand text-white" : "border-white/10 hover:border-white/40 text-zinc-400"}`}
                data-testid={`layout-${key}`}
              >
                <Icon size={14} weight="bold" /> {label}
              </button>
            ))}
          </div>
          <button onClick={() => { setFormInitial(null); setShowForm(true); }} className="btn-primary" data-testid="open-schedule-form">
            <Plus size={16} weight="bold" /> New session
          </button>
        </div>
      </div>

      <AwayThisWeekStrip
        periods={awayPeriods.filter((p) => {
          // Only surface periods that overlap the next 7 days in the top strip;
          // the wider 90-day list feeds the calendar day-cell overlays below.
          const today = new Date(); today.setHours(0, 0, 0, 0);
          const in7 = new Date(today); in7.setDate(in7.getDate() + 7);
          const start = new Date(`${p.start_date}T00:00:00`);
          const end = new Date(`${p.end_date}T23:59:59`);
          return start <= in7 && end >= today;
        })}
        expanded={awayExpanded}
        onToggle={() => setAwayExpanded((v) => !v)}
      />

      <LocationFilterChips
        locations={locationList}
        sessions={sessions}
        value={locationFilter}
        onChange={setLocationFilter}
      />

      {layout === "list" && (
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex" data-testid="schedule-view-toggle">
            {[
              { key: "upcoming", label: "Upcoming", count: upcomingAll.length },
              { key: "archive", label: "Archive", count: archiveAll.length },
            ].map((v) => (
              <button
                key={v.key}
                onClick={() => setView(v.key)}
                className={`tracker px-5 py-2 border ${view === v.key ? "bg-brand border-brand text-white" : "border-white/10 hover:border-white/40 text-zinc-400"}`}
                data-testid={`view-${v.key}`}
              >
                {v.label} ({v.count})
              </button>
            ))}
          </div>
          <span className="text-zinc-700 mx-2">·</span>
          {(view === "upcoming" ? ["all", "scheduled"] : ["all", "completed", "cancelled", "scheduled"]).map((s) => {
            const pool = view === "upcoming" ? upcomingAll : archiveAll;
            const count = s === "all" ? pool.length : pool.filter((x) => x.status === s).length;
            return (
              <button
                key={s}
                onClick={() => setFilter(s)}
                className={`tracker px-4 py-2 border ${filter === s ? "bg-[#1A1A1A] border-white/40" : "border-white/10 hover:border-white/40"}`}
                data-testid={`filter-${s}`}
              >
                {s} ({count})
              </button>
            );
          })}
        </div>
      )}

      {layout !== "list" ? (
        <ScheduleCalendar
          sessions={[...filteredSessions.map((s) => {
            if (!s.assigned_staff_id) return s;
            const st = staff.find((x) => x.id === s.assigned_staff_id);
            return st?.color ? { ...s, _staff_color: st.color } : s;
          }), ...(locationFilter === "all" ? groupItems : groupItems.filter((g) => locationFilter === "__none" ? !g.location : g.location === locationFilter))]}
          view={layout}
          awayPeriods={awayPeriods}
          onCreateAt={openFormAt}
          onAction={onCalendarAction}
          onMove={onSessionMove}
          onResize={onSessionResize}
        />
      ) : (
        <div className="card-flat overflow-x-auto">
        {filtered.length === 0 ? (
          <div className="p-16 text-center text-zinc-500 tracker">No sessions</div>
        ) : (
          <table className="w-full" data-testid="sessions-table">
            <thead>
              <tr className="border-b border-white/10">
                <Th>Time</Th>
                <Th>Client</Th>
                <Th>Type</Th>
                <Th>Staff</Th>
                <Th>Price</Th>
                <Th>Status</Th>
                <Th align="right">Actions</Th>
              </tr>
            </thead>
            <tbody>
              {(() => {
                let lastDay = null;
                const rows = [];
                filtered.forEach((s) => {
                  const dt = new Date(s.start_time);
                  const dayKey = dt.toDateString();
                  if (dayKey !== lastDay) {
                    lastDay = dayKey;
                    const today = new Date().toDateString();
                    const isToday = dayKey === today;
                    rows.push(
                      <tr key={`hdr-${dayKey}`} className="bg-[#0F0F0F] border-y border-white/10" data-testid={`day-header-${dt.toISOString().slice(0,10)}`}>
                        <td colSpan={7} className="px-6 py-3">
                          <div className="flex items-baseline gap-3">
                            <span className={`font-display text-lg ${isToday ? "text-brand" : "text-white"}`}>
                              {dt.toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" })}
                            </span>
                            {isToday && <span className="tracker text-brand-light text-[10px]">TODAY</span>}
                            <span className="tracker text-zinc-600">
                              {filtered.filter((x) => new Date(x.start_time).toDateString() === dayKey).length} session{filtered.filter((x) => new Date(x.start_time).toDateString() === dayKey).length === 1 ? "" : "s"}
                            </span>
                          </div>
                        </td>
                      </tr>
                    );
                  }
                  if (s._group) {
                    const c = s._cls;
                    rows.push(
                      <tr key={s.id} className="border-b border-white/5 hover:bg-[#1A1A1A] cursor-pointer" onClick={() => setViewingClass(c)} data-testid={`schedule-group-row-${c.id}`}>
                        <td className="px-6 py-4 font-mono text-sm">{dt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</td>
                        <td className="px-6 py-4">
                          <div className="font-semibold flex items-center gap-2"><span className="inline-block h-2 w-2 bg-[#8B5CF6]" /> {c.title}</div>
                          <div className="tracker text-[10px] text-zinc-500">{c.booked_count}/{c.capacity} booked{c.waitlist_count ? ` · ${c.waitlist_count} waiting` : ""}{(c.attended_count || c.no_show_count) ? ` · ${c.attended_count || 0} attended · ${c.no_show_count || 0} no-show` : ""}</div>
                        </td>
                        <td className="px-6 py-4 tracker text-[10px] text-[#A78BFA]">GROUP CLASS</td>
                        <td className="px-6 py-4 text-xs">{c.instructor_name || <span className="text-amber-400 tracker text-[10px]">unassigned</span>}</td>
                        <td className="px-6 py-4 font-mono text-sm money">{symbol}{Number(c.price || 0).toFixed(2)} <span className="text-zinc-500 text-[10px]">/ head</span></td>
                        <td className="px-6 py-4"><span className="tracker px-2 py-1 border border-[#8B5CF6]/40 text-[#A78BFA] text-[10px]">{c.spots_left === 0 ? "FULL" : `${c.spots_left} LEFT`}</span></td>
                        <td className="px-6 py-4 text-right"><span className="btn-ghost !py-1.5 !px-2 !text-[10px]">Attendees</span></td>
                      </tr>
                    );
                    return;
                  }
                  const clientTz = s.client?.timezone;
                  const showGhostTime = clientTz && clientTz !== trainerTz;
                  rows.push(
                  <tr
                    key={s.id}
                    className="border-b border-white/5 hover:bg-[#1A1A1A] cursor-pointer"
                    onClick={() => setEditingSession(s)}
                    data-testid={`session-row-${s.id}`}
                  >
                    <Td>
                      <div className="tracker">{dt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</div>
                      {showGhostTime && (
                        <div
                          className="text-[10px] text-brand-light/80 font-mono mt-0.5"
                          title={`Client is in ${clientTz}`}
                          data-testid={`ghost-time-${s.id}`}
                        >
                          {formatTimeInTz(s.start_time, clientTz)} {shortTzLabel(clientTz)} their time
                        </div>
                      )}
                    </Td>
                    <Td><div className="font-semibold">{s.client?.name || "—"}</div><div className="text-xs text-zinc-500">{s.client?.email}</div></Td>
                    <Td>
                      <div>{s.session_type}</div>
                      <div className="text-[10px] text-zinc-500">{s.duration_minutes}m</div>
                      {s.location && (
                        <div className="tracker text-[9px] text-brand-light mt-0.5 inline-flex items-center gap-0.5" data-testid={`row-loc-${s.id}`}>
                          <MapPin size={9} weight="bold" /> {s.location}
                        </div>
                      )}
                    </Td>
                    <Td>
                      {s.staff ? (
                        <div>
                          <div className="text-xs font-semibold">{s.staff.name}</div>
                          <div className="text-[10px] text-zinc-500"><span className="money">pays {symbol}{Number(s.staff_pay || 0).toFixed(2)}</span>{s.payroll_run_id ? " · locked" : ""}</div>
                        </div>
                      ) : (
                        <span className="text-zinc-600 text-xs">—</span>
                      )}
                    </Td>
                    <Td><span className="money">{symbol}{(s.price || 0).toFixed(2)}</span></Td>
                    <Td>
                      <div className="flex items-center gap-2">
                        <StatusPill status={s.status} />
                        {s.reminder_sent_at && (
                          <span className="tracker px-1.5 py-0.5 border border-brand/30 text-brand-light text-[9px]" title={`Reminded ${new Date(s.reminder_sent_at).toLocaleString()}`} data-testid={`reminder-badge-${s.id}`}>
                            ✓ 24H
                          </span>
                        )}
                      </div>
                    </Td>
                    <Td align="right">
                      <div className="flex items-center gap-2 justify-end" onClick={(e) => e.stopPropagation()}>
                        {s.status === "scheduled" && (
                          <>
                            <button onClick={() => setEditingSession(s)} className="btn-ghost !py-1.5 !px-2 !text-[10px]" data-testid={`edit-${s.id}`} title="Edit session">
                              <PencilSimple size={14} weight="bold" />
                            </button>
                            <button onClick={() => sendReminder(s.id)} className="btn-ghost !py-1.5 !px-2 !text-[10px]" data-testid={`remind-${s.id}`} title="Send reminder">
                              <Bell size={14} weight="bold" />
                            </button>
                            <button onClick={() => downloadIcs(s.id)} className="btn-ghost !py-1.5 !px-2 !text-[10px]" data-testid={`ics-${s.id}`} title="Add to calendar (.ics)">
                              <DownloadSimple size={14} weight="bold" />
                            </button>
                            <button onClick={() => updateStatus(s.id, "completed")} className="btn-ghost !py-1.5 !px-2 !text-[10px]" data-testid={`complete-${s.id}`} title="Mark completed">
                              <CheckCircle size={14} weight="bold" />
                            </button>
                            <button onClick={() => updateStatus(s.id, "no_show")} className="btn-ghost !py-1.5 !px-2 !text-[10px] text-amber-400" data-testid={`no-show-${s.id}`} title="Mark no-show (auto-credits client)">
                              NS
                            </button>
                            <button onClick={() => requestCancel(s)} className="btn-ghost !py-1.5 !px-2 !text-[10px]" title="Cancel">
                              <X size={14} weight="bold" />
                            </button>
                          </>
                        )}
                        <button onClick={() => requestDelete(s)} className="btn-ghost !py-1.5 !px-2 !text-[10px]" data-testid={`delete-session-${s.id}`} title="Delete">
                          <Trash size={14} weight="bold" />
                        </button>
                      </div>
                    </Td>
                  </tr>
                  );
                });
                return rows;
              })()}
            </tbody>
          </table>
        )}
        </div>
      )}

      {showForm && <ScheduleForm onClose={closeForm} onCreated={load} clients={clients} staff={staff} initialDate={formInitial} />}
      {viewingClass && <GroupBookingsModal cls={groupClasses.find((c) => c.id === viewingClass.id) || viewingClass} onClose={() => setViewingClass(null)} onChanged={load} />}

      {editingSession && (
        <EditSessionModal
          session={editingSession}
          staff={staff}
          onClose={() => setEditingSession(null)}
          onSaved={() => { setEditingSession(null); load(); }}
        />
      )}

      {cancellingSession && (
        <SeriesCancelDialog
          session={cancellingSession.session}
          action={cancellingSession.action}
          onClose={() => setCancellingSession(null)}
          onConfirm={confirmCancelOrDelete}
        />
      )}

      <CalendarSync />
    </div>
  );
}

function ScheduleForm({ onClose, onCreated, clients, staff = [], initialDate = null }) {
  const { symbol } = useCurrency();
  const initialType = SESSION_TYPES[0];
  const toLocalInput = (d) => {
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };
  const initDateObj = initialDate?.date || (initialDate instanceof Date ? initialDate : null);
  const draggedDuration = initialDate?.duration || null;
  const matchedType = draggedDuration ? SESSION_TYPES.find((t) => t.duration === draggedDuration) : null;
  const seedType = matchedType || initialType;

  const blankRow = (overrides = {}) => ({
    id: Math.random().toString(36).slice(2),
    start_time: initDateObj ? toLocalInput(initDateObj) : "",
    duration_minutes: draggedDuration || seedType.duration,
    session_type: seedType.label,
    price: seedType.defaultPrice,
    location: "",
    repeat_weeks: 0,
    repeat_interval: 1,
    repeat_mode: "preset", // "preset" | "until"
    repeat_until: "",       // YYYY-MM-DD when repeat_mode === "until"
    ...overrides,
  });

  const [clientId, setClientId] = useState(clients[0]?.id || "");
  const [rows, setRows] = useState([blankRow()]);
  const [notes, setNotes] = useState("");
  const [assignedStaff, setAssignedStaff] = useState("");
  const [loading, setLoading] = useState(false);
  const [locations, setLocations] = useState([]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/locations");
        if (alive) setLocations(data || []);
      } catch { /* noop */ }
    })();
    return () => { alive = false; };
  }, []);

  // Keep repeat_weeks in sync when the user picks "until date…" mode.
  // Counts additional occurrences AFTER the first session, so:
  //   repeat_weeks = floor((end_date − start_date) / (interval × 7 days))
  useEffect(() => {
    setRows((rs) =>
      rs.map((r) => {
        if (r.repeat_mode !== "until") return r;
        if (!r.start_time || !r.repeat_until) {
          return r.repeat_weeks === 0 ? r : { ...r, repeat_weeks: 0 };
        }
        const start = new Date(r.start_time);
        const end = new Date(`${r.repeat_until}T23:59:59`);
        if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end <= start) {
          return r.repeat_weeks === 0 ? r : { ...r, repeat_weeks: 0 };
        }
        const intervalDays = (Number(r.repeat_interval) || 1) * 7;
        const diffDays = Math.floor((end - start) / (1000 * 60 * 60 * 24));
        const extra = Math.max(0, Math.floor(diffDays / intervalDays));
        return r.repeat_weeks === extra ? r : { ...r, repeat_weeks: extra };
      })
    );
  }, [
    rows.map((r) => `${r.id}|${r.repeat_mode}|${r.start_time}|${r.repeat_until}|${r.repeat_interval}`).join("~"),
  ]);

  const selectedClient = clients.find((c) => c.id === clientId);
  const currentBalance = Number(selectedClient?.balance || 0);

  // Iter 57 — prefill each row's location from the client's default_location
  // whenever the client changes, but ONLY if the row hasn't been manually set.
  const clientDefaultLoc = selectedClient?.default_location || "";
  useEffect(() => {
    if (!clientDefaultLoc) return;
    setRows((rs) => rs.map((r) => (r.location ? r : { ...r, location: clientDefaultLoc })));
  }, [clientDefaultLoc]);

  // Total cost across all rows + their recurring occurrences
  const totalSessions = rows.reduce((n, r) => n + (1 + Number(r.repeat_weeks || 0)), 0);
  const totalCost = rows.reduce(
    (sum, r) => sum + Number(r.price || 0) * (1 + Number(r.repeat_weeks || 0)),
    0
  );
  const remainingAfter = currentBalance - totalCost;

  const updateRow = (id, patch) => setRows((rs) => rs.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  const addRow = () => {
    const last = rows[rows.length - 1];
    // Seed new row's date 1 day later by default for ergonomics
    let nextDate = "";
    if (last?.start_time) {
      const d = new Date(last.start_time);
      d.setDate(d.getDate() + 1);
      nextDate = toLocalInput(d);
    }
    setRows((rs) => [...rs, blankRow({ start_time: nextDate, duration_minutes: last.duration_minutes, session_type: last.session_type, price: last.price })]);
  };
  const removeRow = (id) => setRows((rs) => (rs.length > 1 ? rs.filter((r) => r.id !== id) : rs));

  const applyType = (id, label) => {
    const t = SESSION_TYPES.find((x) => x.label === label) || SESSION_TYPES[0];
    updateRow(id, { session_type: t.label, duration_minutes: t.duration, price: t.defaultPrice });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!clientId) return toast.error("Add a client first");
    if (rows.some((r) => !r.start_time)) return toast.error("Every row needs a date/time");
    const badUntil = rows.find(
      (r) => r.repeat_mode === "until" && (!r.repeat_until || new Date(`${r.repeat_until}T23:59:59`) <= new Date(r.start_time))
    );
    if (badUntil) {
      return toast.error("End date must be after the session start time");
    }
    setLoading(true);
    try {
      const items = rows.map((r) => ({
        start_time: new Date(r.start_time).toISOString(),
        duration_minutes: Number(r.duration_minutes),
        session_type: r.session_type,
        price: Number(r.price),
        notes: notes || "",
        location: r.location || null,
        assigned_staff_id: assignedStaff || null,
        repeat_weeks: Number(r.repeat_weeks || 0),
        repeat_interval: Number(r.repeat_interval || 1),
      }));
      const { data } = await api.post("/sessions/batch", { client_id: clientId, items });
      toast.success(`Scheduled ${data.count} session${data.count === 1 ? "" : "s"}`);
      onCreated();
      onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="schedule-form-modal">
      <div className="card-flat w-full max-w-2xl p-6 sm:p-8 my-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="tracker text-brand mb-2">[ New sessions ]</div>
            <h2 className="font-display text-3xl">Schedule.</h2>
          </div>
          <button onClick={onClose} className="text-zinc-400 hover:text-white"><X size={20} weight="bold" /></button>
        </div>

        <form onSubmit={submit} className="space-y-5">
          <div>
            <label className="tracker block mb-2">Client</label>
            <ClientSearchSelect
              clients={clients}
              value={clientId}
              onChange={setClientId}
            />
          </div>

          {selectedClient && (
            <div className={`card-flat p-3 flex items-center justify-between ${remainingAfter < 0 ? "border-[#FF3B30]/40" : ""}`} data-testid="session-balance-preview">
              <div>
                <div className="tracker mb-1">Pot balance</div>
                <div className="font-mono text-sm money">{symbol}{currentBalance.toFixed(2)}</div>
              </div>
              <div className="text-right">
                <div className="tracker mb-1">After {totalSessions} session{totalSessions === 1 ? "" : "s"}</div>
                <div className={`font-mono text-sm money ${remainingAfter < 0 ? "text-[#FF6B61]" : "text-emerald-400"}`}>
                  {symbol}{remainingAfter.toFixed(2)}
                </div>
              </div>
            </div>
          )}

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <label className="tracker">Dates · times · repeat</label>
              <span className="tracker text-zinc-500 text-[10px]">{rows.length} row{rows.length === 1 ? "" : "s"} → {totalSessions} session{totalSessions === 1 ? "" : "s"} · <span className="money">{symbol}{totalCost.toFixed(2)}</span></span>
            </div>
            {rows.map((r, idx) => (
              <SessionRow
                key={r.id}
                row={r}
                idx={idx}
                locations={locations}
                onChange={(patch) => updateRow(r.id, patch)}
                onTypeChange={(label) => applyType(r.id, label)}
                onRemove={() => removeRow(r.id)}
                canRemove={rows.length > 1}
              />
            ))}
            <button
              type="button"
              onClick={addRow}
              className="w-full border border-dashed border-white/15 hover:border-brand hover:bg-brand/5 transition-colors py-3 tracker text-zinc-400 hover:text-white flex items-center justify-center gap-2"
              data-testid="add-session-row"
            >
              <Plus size={14} weight="bold" /> Add another date
            </button>
          </div>

          <div>
            <label className="tracker block mb-2">Notes (applied to all)</label>
            <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} className="w-full px-4 py-3 border" data-testid="session-notes" />
          </div>

          <div>
            <label className="tracker block mb-2">Assign to staff (optional, applied to all)</label>
            <select value={assignedStaff} onChange={(e) => setAssignedStaff(e.target.value)} className="w-full px-4 py-3 border" data-testid="session-staff-select">
              <option value="">— Unassigned (no payroll cost) —</option>
              {staff.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>

          <button type="submit" disabled={loading} className="btn-primary w-full justify-center" data-testid="session-submit">
            {loading ? "Scheduling…" : `Schedule ${totalSessions} session${totalSessions === 1 ? "" : "s"} · debit ${symbol}${totalCost.toFixed(2)}`}
          </button>
        </form>
      </div>
    </div>
  );
}

function EditSessionModal({ session, staff = [], onClose, onSaved }) {
  const { symbol } = useCurrency();
  const toLocalInput = (d) => {
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  const [startTime, setStartTime] = useState(toLocalInput(new Date(session.start_time)));
  const [sessionType, setSessionType] = useState(session.session_type || "60 Minutes");
  const [duration, setDuration] = useState(session.duration_minutes || 60);
  const [price, setPrice] = useState(session.price ?? 80);
  const [assignedStaff, setAssignedStaff] = useState(session.assigned_staff_id || "");
  const [notes, setNotes] = useState(session.notes || "");
  const [location, setLocation] = useState(session.location || "");
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/locations");
        if (alive) setLocations(data || []);
      } catch { /* noop */ }
    })();
    return () => { alive = false; };
  }, []);

  const locked = !!session.payroll_run_id;

  const applyType = (label) => {
    const t = SESSION_TYPES.find((x) => x.label === label) || SESSION_TYPES[0];
    setSessionType(t.label);
    setDuration(t.duration);
    setPrice(t.defaultPrice);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!startTime) return toast.error("Date/time required");
    setLoading(true);
    try {
      const payload = {
        start_time: new Date(startTime).toISOString(),
        duration_minutes: Number(duration),
        session_type: sessionType,
        price: Number(price),
        notes,
        location: location || null,
        assigned_staff_id: assignedStaff || null,
      };
      await api.patch(`/sessions/${session.id}`, payload);
      toast.success("Session updated");
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="edit-session-modal">
      <div className="card-flat w-full max-w-xl p-6 sm:p-8 my-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="tracker text-brand mb-2">[ Edit session ]</div>
            <h2 className="font-display text-3xl">{session.client?.name || "Session"}.</h2>
          </div>
          <button onClick={onClose} className="text-zinc-400 hover:text-white" data-testid="edit-session-close"><X size={20} weight="bold" /></button>
        </div>

        {locked && (
          <div className="card-flat p-3 mb-4 border-[#FF3B30]/40 text-[#FF6B61] text-xs tracker" data-testid="edit-session-locked-warning">
            This session is locked in a payroll run · price &amp; staff cannot change
          </div>
        )}

        <form onSubmit={submit} className="space-y-5">
          <div className="grid grid-cols-2 gap-2" data-testid="edit-type-grid">
            {SESSION_TYPES.map((t) => (
              <button
                type="button"
                key={t.label}
                onClick={() => applyType(t.label)}
                disabled={locked}
                className={`p-2 border text-left ${sessionType === t.label ? "border-brand bg-brand/10" : "border-white/10 hover:border-white/40"} ${locked ? "opacity-50 cursor-not-allowed" : ""}`}
                data-testid={`edit-type-${t.label.replace(/\s+/g, "-").toLowerCase()}`}
              >
                <div className="font-display text-sm leading-none">{t.label}</div>
                <div className="tracker text-zinc-500 text-[10px] mt-1"><span className="money">{symbol}{t.defaultPrice}</span> · {t.duration}m</div>
              </button>
            ))}
          </div>

          <div>
            <label className="tracker block mb-2">Date &amp; time</label>
            <input
              required
              type="datetime-local"
              step="900"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              className="w-full px-4 py-3 border"
              data-testid="edit-session-datetime"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="tracker block mb-2">Duration (min)</label>
              <input
                type="number"
                min="15"
                step="15"
                value={duration}
                onChange={(e) => setDuration(e.target.value)}
                className="w-full px-4 py-3 border"
                data-testid="edit-session-duration"
              />
            </div>
            <div>
              <label className="tracker block mb-2">Price ({symbol})</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                disabled={locked}
                className="w-full px-4 py-3 border disabled:opacity-50"
                data-testid="edit-session-price"
              />
            </div>
          </div>

          <div>
            <label className="tracker block mb-2">Assign to staff</label>
            <select
              value={assignedStaff}
              onChange={(e) => setAssignedStaff(e.target.value)}
              disabled={locked}
              className="w-full px-4 py-3 border disabled:opacity-50"
              data-testid="edit-session-staff"
            >
              <option value="">— Unassigned —</option>
              {staff.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="tracker block mb-2">Location</label>
            <select
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              className="w-full px-4 py-3 border bg-[#0A0A0A]"
              data-testid="edit-session-location"
            >
              <option value="">— No location —</option>
              {locations.map((loc) => (
                <option key={loc.id} value={loc.name}>{loc.name}</option>
              ))}
              {location && !locations.some((l) => l.name === location) && (
                <option value={location}>{location} (removed)</option>
              )}
            </select>
          </div>

          <div>
            <label className="tracker block mb-2">Notes</label>
            <textarea
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="w-full px-4 py-3 border"
              data-testid="edit-session-notes"
            />
          </div>

          <button type="submit" disabled={loading} className="btn-primary w-full justify-center" data-testid="edit-session-submit">
            {loading ? "Saving…" : "Save changes"}
          </button>
        </form>
      </div>
    </div>
  );
}

function SessionRow({ row, idx, locations = [], onChange, onTypeChange, onRemove, canRemove }) {
  const { symbol } = useCurrency();
  return (
    <div className="card-flat p-3 space-y-3" data-testid={`session-form-row-${idx}`}>
      <div className="flex items-center justify-between">
        <span className="tracker text-zinc-500 text-[10px]">SESSION {idx + 1}</span>
        {canRemove && (
          <button type="button" onClick={onRemove} className="text-zinc-500 hover:text-[#FF6B61]" data-testid={`remove-row-${idx}`}>
            <Trash size={14} weight="bold" />
          </button>
        )}
      </div>

      <div className="grid grid-cols-2 gap-2" data-testid={`type-grid-${idx}`}>
        {SESSION_TYPES.map((t) => (
          <button
            type="button"
            key={t.label}
            onClick={() => onTypeChange(t.label)}
            className={`p-2 border text-left ${row.session_type === t.label ? "border-brand bg-brand/10" : "border-white/10 hover:border-white/40"}`}
            data-testid={`row-${idx}-type-${t.label.replace(/\s+/g, "-").toLowerCase()}`}
          >
            <div className="font-display text-sm leading-none">{t.label}</div>
            <div className="tracker text-zinc-500 text-[10px] mt-1"><span className="money">{symbol}{t.defaultPrice}</span> · {t.duration}m</div>
          </button>
        ))}
      </div>

      <div className="grid sm:grid-cols-3 gap-2">
        <div className="sm:col-span-3">
          <input
            required
            type="datetime-local"
            step="900"
            value={row.start_time}
            onChange={(e) => onChange({ start_time: e.target.value })}
            className="w-full px-3 py-2 border text-sm"
            data-testid={`row-${idx}-datetime`}
          />
        </div>
        <div>
          <label className="tracker block mb-1 text-[10px]">Duration</label>
          <input type="number" min="15" step="15" value={row.duration_minutes} onChange={(e) => onChange({ duration_minutes: e.target.value })} className="w-full px-3 py-2 border text-sm" data-testid={`row-${idx}-duration`} />
        </div>
        <div>
          <label className="tracker block mb-1 text-[10px]">Price ($)</label>
          <input type="number" min="0" step="0.01" value={row.price} onChange={(e) => onChange({ price: e.target.value })} className="w-full px-3 py-2 border text-sm" data-testid={`row-${idx}-price`} />
        </div>
        <div>
          <label className="tracker block mb-1 text-[10px]">Repeat</label>
          <select
            value={
              row.repeat_mode === "until"
                ? `until_${row.repeat_interval || 1}`
                : `${row.repeat_weeks}_${row.repeat_interval}`
            }
            onChange={(e) => {
              const v = e.target.value;
              if (v.startsWith("until_")) {
                const interval = Number(v.split("_")[1]) || 1;
                onChange({
                  repeat_mode: "until",
                  repeat_interval: interval,
                  // keep repeat_until as-is; weeks recomputed by parent useEffect
                });
              } else {
                const [w, i] = v.split("_").map(Number);
                onChange({
                  repeat_mode: "preset",
                  repeat_weeks: w,
                  repeat_interval: i,
                  repeat_until: "",
                });
              }
            }}
            className="w-full px-3 py-2 border text-sm"
            data-testid={`row-${idx}-repeat`}
          >
            <option value="0_1">Once</option>
            <option value="3_1">Weekly × 4</option>
            <option value="7_1">Weekly × 8</option>
            <option value="11_1">Weekly × 12</option>
            <option value="23_1">Weekly × 24</option>
            <option value="3_2">Bi-weekly × 4</option>
            <option value="7_2">Bi-weekly × 8</option>
            <option value="11_2">Bi-weekly × 12</option>
            <option value="until_1">Weekly — until date…</option>
            <option value="until_2">Bi-weekly — until date…</option>
          </select>
        </div>
      </div>

      <div>
        <label className="tracker block mb-1 text-[10px]">Location (optional)</label>
        <select
          value={row.location || ""}
          onChange={(e) => onChange({ location: e.target.value })}
          className="w-full px-3 py-2 border text-sm bg-[#0A0A0A]"
          data-testid={`row-${idx}-location`}
        >
          <option value="">— No location —</option>
          {locations.map((loc) => (
            <option key={loc.id} value={loc.name}>{loc.name}</option>
          ))}
          {row.location && !locations.some((l) => l.name === row.location) && (
            <option value={row.location}>{row.location} (removed)</option>
          )}
        </select>
        {locations.length === 0 && (
          <div className="tracker text-[9px] text-zinc-600 mt-1">Add locations in Settings → Session locations.</div>
        )}
      </div>

      {row.repeat_mode === "until" && (
        <div className="grid sm:grid-cols-2 gap-2 pt-1" data-testid={`row-${idx}-until-block`}>
          <div>
            <label className="tracker block mb-1 text-[10px]">END DATE</label>
            <Popover>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className="w-full px-3 py-2 border text-sm text-left flex items-center justify-between bg-[#0A0A0A] hover:border-white/40"
                  data-testid={`row-${idx}-repeat-until`}
                >
                  <span className={row.repeat_until ? "font-mono" : "text-zinc-500"}>
                    {row.repeat_until || "Pick end date…"}
                  </span>
                  <CalendarBlank size={14} weight="bold" className="text-zinc-400" />
                </button>
              </PopoverTrigger>
              <PopoverContent
                align="start"
                sideOffset={6}
                className="w-auto p-0 bg-[#0A0A0A] border-white/10 z-[60]"
                data-testid={`row-${idx}-repeat-until-popover`}
              >
                <Calendar
                  mode="single"
                  selected={row.repeat_until ? new Date(`${row.repeat_until}T12:00:00`) : undefined}
                  defaultMonth={row.repeat_until ? new Date(`${row.repeat_until}T12:00:00`) : (row.start_time ? new Date(row.start_time) : undefined)}
                  onSelect={(d) => {
                    if (!d) return;
                    const pad = (n) => String(n).padStart(2, "0");
                    const v = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
                    onChange({ repeat_until: v });
                  }}
                  disabled={(d) => {
                    if (!row.start_time) return false;
                    const start = new Date(row.start_time);
                    const day = new Date(d);
                    day.setHours(23, 59, 59, 999);
                    return day <= start;
                  }}
                />
              </PopoverContent>
            </Popover>
          </div>
          <div className="text-xs text-zinc-400 sm:pt-5">
            {(() => {
              const total = 1 + Number(row.repeat_weeks || 0);
              const intervalLabel = Number(row.repeat_interval) === 2 ? "bi-weekly" : "weekly";
              if (!row.repeat_until || !row.start_time) {
                return <span className="text-zinc-500">Pick a start time and end date to see the count.</span>;
              }
              return (
                <span data-testid={`row-${idx}-until-count`}>
                  → {total} session{total === 1 ? "" : "s"} ({intervalLabel})
                </span>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
}

function Th({ children, align = "left" }) {
  return <th className={`tracker text-${align} px-6 py-3 font-normal`}>{children}</th>;
}
function Td({ children, align = "left" }) {
  return <td className={`px-6 py-4 text-${align} text-sm`}>{children}</td>;
}

function LocationChip({ v, label, count, active, testid, onClick }) {
  return (
    <button
      type="button"
      onClick={() => onClick(v)}
      className={`tracker text-[10px] px-3 py-1.5 border transition-colors ${
        active
          ? "bg-brand border-brand text-white"
          : "border-white/10 text-zinc-400 hover:text-white hover:border-white/30"
      }`}
      data-testid={testid}
    >
      {label} {count != null && <span className="opacity-60 ml-1 font-mono">{count}</span>}
    </button>
  );
}

function LocationFilterChips({ locations, sessions, value, onChange }) {
  // Only render if the trainer actually uses locations (2+ options or has tagged
  // at least one session). Silence-by-default keeps the calendar clean for solo coaches.
  const usedLocations = useMemo(() => {
    const set = new Set();
    (sessions || []).forEach((s) => { if (s.location) set.add(s.location); });
    return set;
  }, [sessions]);
  const chips = useMemo(() => {
    const seen = new Set();
    const list = [];
    (locations || []).forEach((l) => {
      if (!seen.has(l.name)) { list.push({ id: l.id, name: l.name }); seen.add(l.name); }
    });
    // Include any location strings on sessions whose backing row was soft-deleted
    usedLocations.forEach((n) => { if (!seen.has(n)) { list.push({ id: `orphan-${n}`, name: n, orphan: true }); seen.add(n); } });
    return list;
  }, [locations, usedLocations]);
  const untaggedCount = useMemo(
    () => (sessions || []).filter((s) => !s.location).length,
    [sessions]
  );
  if (chips.length < 2 && usedLocations.size === 0) return null; // no meaningful filter to offer
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="location-filter-chips">
      <span className="tracker text-[10px] text-zinc-600 mr-1">[ LOCATION ]</span>
      <LocationChip v="all" label="All" count={sessions.length} active={value === "all"} testid="loc-chip-all" onClick={onChange} />
      {chips.map((c) => {
        const count = sessions.filter((s) => s.location === c.name).length;
        return (
          <LocationChip
            key={c.id}
            v={c.name}
            label={c.orphan ? `${c.name} (removed)` : c.name}
            count={count}
            active={value === c.name}
            testid={`loc-chip-${c.name}`}
            onClick={onChange}
          />
        );
      })}
      {untaggedCount > 0 && (
        <LocationChip v="__none" label="Untagged" count={untaggedCount} active={value === "__none"} testid="loc-chip-none" onClick={onChange} />
      )}
    </div>
  );
}

function AwayThisWeekStrip({ periods, expanded, onToggle }) {
  if (!periods || periods.length === 0) {
    return (
      <div
        className="card-flat px-4 py-3 flex items-center gap-3 border-white/10 text-zinc-500"
        data-testid="away-strip-empty"
      >
        <AirplaneTilt size={16} weight="duotone" className="text-zinc-600" />
        <span className="tracker text-[11px]">No clients away in the next 7 days</span>
      </div>
    );
  }
  const fmt = (d) => {
    const dt = new Date(`${d}T12:00:00`);
    return dt.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  };
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return (
    <div
      className="card-flat border-brand/40 bg-brand/5"
      data-testid="away-strip"
    >
      <button
        type="button"
        onClick={onToggle}
        className="w-full px-4 py-3 flex items-center justify-between gap-3 hover:bg-brand/10"
        data-testid="away-strip-toggle"
      >
        <div className="flex items-center gap-3">
          <AirplaneTilt size={18} weight="duotone" className="text-brand-light" />
          <span className="tracker text-brand-light text-[11px]">[ AWAY · NEXT 7 DAYS ]</span>
          <span className="font-display text-lg leading-none" data-testid="away-strip-count">
            {periods.length} client{periods.length === 1 ? "" : "s"}
          </span>
        </div>
        {expanded
          ? <CaretUp size={14} weight="bold" className="text-zinc-400" />
          : <CaretDown size={14} weight="bold" className="text-zinc-400" />}
      </button>
      {expanded && (
        <div
          className="px-4 pb-4 pt-1 grid sm:grid-cols-2 lg:grid-cols-3 gap-2"
          data-testid="away-strip-list"
        >
          {periods.map((p) => {
            const start = new Date(`${p.start_date}T00:00:00`);
            const isActive = start <= today;
            return (
              <div
                key={p.id}
                className="border border-white/10 bg-[#0A0A0A] px-3 py-2 flex items-start justify-between gap-2"
                data-testid={`away-strip-row-${p.id}`}
              >
                <div className="min-w-0">
                  <div className="font-display text-sm truncate">
                    {p.client?.name || "Client"}
                  </div>
                  <div className="tracker text-zinc-500 text-[10px] font-mono mt-0.5">
                    {fmt(p.start_date)} → {fmt(p.end_date)}
                  </div>
                  {p.reason && (
                    <div className="text-xs text-zinc-400 mt-1 truncate" title={p.reason}>
                      {p.reason}
                    </div>
                  )}
                </div>
                {isActive && (
                  <span className="tracker px-2 py-0.5 border border-emerald-500/40 text-emerald-400 text-[9px] shrink-0">
                    NOW
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    scheduled: "bg-brand/20 text-brand-light border-brand/40",
    completed: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
    cancelled: "bg-zinc-500/10 text-zinc-400 border-zinc-500/30",
    no_show: "bg-amber-500/10 text-amber-400 border-amber-500/30",
    unpaid: "bg-[#FF3B30]/10 text-[#FF6B61] border-[#FF3B30]/30",
    paid: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
  };
  const labelMap = { unpaid: "unpaid", paid: "paid", no_show: "no-show" };
  const label = labelMap[status] || status;
  return <span className={`tracker px-2 py-1 border ${map[status] || ""}`}>{label}</span>;
}

export { Th, Td, StatusPill, LocationChip };

function ClientSearchSelect({ clients, value, onChange }) {
  const { symbol } = useCurrency();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIdx, setActiveIdx] = useState(0);
  const wrapRef = useRef(null);

  const selected = clients.find((c) => c.id === value);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return clients;
    return clients.filter((c) =>
      (c.name || "").toLowerCase().includes(q) ||
      (c.email || "").toLowerCase().includes(q) ||
      (c.phone || "").toLowerCase().includes(q)
    );
  }, [clients, query]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const fn = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", fn);
    return () => document.removeEventListener("mousedown", fn);
  }, [open]);

  const pick = (c) => {
    onChange(c.id);
    setQuery("");
    setOpen(false);
  };

  const onKey = (e) => {
    if (!open && (e.key === "ArrowDown" || e.key === "Enter")) { setOpen(true); return; }
    if (!open) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActiveIdx((i) => Math.min(i + 1, filtered.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActiveIdx((i) => Math.max(i - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); if (filtered[activeIdx]) pick(filtered[activeIdx]); }
    else if (e.key === "Escape") { setOpen(false); }
  };

  if (clients.length === 0) {
    return <div className="w-full px-4 py-3 border text-zinc-500 text-sm">Add a client first</div>;
  }

  return (
    <div ref={wrapRef} className="relative" data-testid="session-client-search">
      {/* Trigger */}
      <button
        type="button"
        onClick={() => { setOpen((o) => !o); setQuery(""); setActiveIdx(0); }}
        onKeyDown={onKey}
        className="w-full px-4 py-3 border flex items-center justify-between bg-[#0A0A0A] hover:border-white/30 focus:border-brand outline-none text-left"
        data-testid="session-client-trigger"
      >
        {selected ? (
          <span className="flex items-center gap-3">
            <span className="font-semibold">{selected.name}</span>
            <span className="font-mono text-xs text-zinc-500 money">{symbol}{Number(selected.balance || 0).toFixed(2)} pot</span>
          </span>
        ) : (
          <span className="text-zinc-500">Pick a client…</span>
        )}
        <CaretDown size={12} weight="bold" className={`text-zinc-500 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="absolute z-30 mt-1 w-full card-flat border border-white/10 shadow-xl">
          <div className="relative p-2 border-b border-white/5">
            <MagnifyingGlass size={12} weight="bold" className="absolute left-5 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none" />
            <input
              autoFocus
              type="search"
              value={query}
              onChange={(e) => { setQuery(e.target.value); setActiveIdx(0); }}
              onKeyDown={onKey}
              placeholder="Search name, email or phone…"
              className="w-full pl-8 pr-3 py-2 bg-[#0F0F0F] border border-white/10 focus:border-brand outline-none text-sm"
              data-testid="session-client-search-input"
            />
          </div>
          <div className="max-h-64 overflow-y-auto" data-testid="session-client-results">
            {filtered.length === 0 ? (
              <div className="px-4 py-6 text-center text-zinc-500 text-sm tracker">No matches</div>
            ) : (
              filtered.map((c, i) => (
                <button
                  type="button"
                  key={c.id}
                  onMouseEnter={() => setActiveIdx(i)}
                  onClick={() => pick(c)}
                  className={`w-full text-left px-4 py-2.5 flex items-center justify-between gap-3 ${i === activeIdx ? "bg-brand/20" : "hover:bg-[#1A1A1A]"} ${c.id === value ? "border-l-2 border-brand" : ""}`}
                  data-testid={`session-client-option-${c.id}`}
                >
                  <div className="min-w-0">
                    <div className="font-semibold text-sm truncate">{c.name}</div>
                    <div className="font-mono text-[10px] text-zinc-500 truncate">{c.email}{c.phone ? ` · ${c.phone}` : ""}</div>
                  </div>
                  <div className={`font-mono text-xs whitespace-nowrap money ${Number(c.balance || 0) < 0 ? "text-[#FF6B61]" : "text-emerald-400"}`}>
                    {symbol}{Number(c.balance || 0).toFixed(2)}
                  </div>
                </button>
              ))
            )}
          </div>
          <div className="px-3 py-1.5 border-t border-white/5 tracker text-zinc-600 text-[9px] flex items-center justify-between">
            <span>{filtered.length} of {clients.length}</span>
            <span>↑↓ navigate · ↵ pick · esc close</span>
          </div>
        </div>
      )}
    </div>
  );
}
